import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { SeoHead } from "@/components/SeoHead";
import { PageTransition } from "@/components/layout/PageTransition";
import { motion } from "framer-motion";
import { Calendar, ArrowRight, Newspaper, Tag } from "lucide-react";
import { useCms } from "@/hooks/useCms";
import { db } from "@/lib/firebase";
import { collection, getDocs, query, where, orderBy } from "firebase/firestore";

type Post = {
  id: string; title: string; slug: string; excerpt: string;
  cover_image_url: string; status: string; category: string;
  published_at: string | null; created_at: string;
};

const CATEGORY_STYLES: Record<string, string> = {
  "General":       "bg-gray-700/60 text-gray-200",
  "News":          "bg-blue-900/60 text-blue-300",
  "Achievements":  "bg-amber-900/60 text-amber-300",
  "Insights":      "bg-purple-900/60 text-purple-300",
  "Events":        "bg-pink-900/60 text-pink-300",
  "Academy":       "bg-emerald-900/60 text-emerald-300",
  "Transfer News": "bg-orange-900/60 text-orange-300",
  "Scouting":      "bg-cyan-900/60 text-cyan-300",
};

function CategoryBadge({ category }: { category: string }) {
  const cls = CATEGORY_STYLES[category] ?? CATEGORY_STYLES["General"];
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full uppercase tracking-widest ${cls}`}>
      <Tag size={9} />{category}
    </span>
  );
}

function formatDate(d: string) {
  return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

export default function Blog() {
  const { data: posts = [], isLoading } = useQuery<Post[]>({
    queryKey: ["/api/content", "/api/blog"],
    queryFn: async () => {
      try {
        const q = query(
          collection(db, "blog_posts"),
          where("status", "==", "published"),
          orderBy("published_at", "desc")
        );
        const snap = await getDocs(q);
        return snap.docs.map(d => {
          const item = d.data();
          return {
            id: d.id,
            title: item.title || "",
            slug: item.slug || "",
            excerpt: item.excerpt || "",
            cover_image_url: item.cover_image_url || "",
            status: item.status || "published",
            category: item.category || "General",
            published_at: item.published_at || "",
            created_at: item.created_at || "",
          };
        });
      } catch (err) {
        console.error("Failed to fetch blog posts from Firestore:", err);
        return [];
      }
    },
  });

  const { getMeta } = useCms();
  const heroMeta = getMeta<{ heading: string; subtext: string; badge: string }>("blog_hero", {
    heading: "BLOG & NEWS",
    subtext: "Agency updates, athlete milestones, and insights from the world of sports management.",
    badge: "Latest Updates"
  });

  const allCategories = ["All", ...Array.from(new Set(posts.map(p => p.category ?? "General").filter(Boolean)))];
  const [activeCategory, setActiveCategory] = useState("All");

  const filtered = activeCategory === "All" ? posts : posts.filter(p => (p.category ?? "General") === activeCategory);
  const [featured, ...rest] = filtered;

  return (
    <>
      <SeoHead title="Blog & News" description="Stay up to date with the latest news, athlete achievements, and insights from Eliada Sports Agency." path="/blog" />
      <PageTransition className="pt-24 pb-24">

        {/* Hero */}
        <div className="relative py-20 bg-secondary overflow-hidden border-b border-white/5">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-transparent" />
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-primary/30 bg-primary/10 text-primary mb-6">
              <Newspaper size={13} />
              <span className="text-xs font-display tracking-widest uppercase font-semibold">{heroMeta.badge}</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-display font-bold text-white mb-4">
              {heroMeta.heading.split(" ").map((w, i, arr) => 
                i === arr.length - 1 ? <span key={i} className="text-primary">&amp; {w.replace("&", "")}</span> : <span key={i}>{w} </span>
              )}
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              {heroMeta.subtext}
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12">

          {/* Category filter tabs */}
          {!isLoading && posts.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap mb-10">
              {allCategories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-4 py-2 rounded-full text-sm font-semibold transition-all border ${
                    activeCategory === cat
                      ? "bg-primary text-black border-primary"
                      : "bg-secondary border-white/10 text-muted-foreground hover:border-primary/40 hover:text-white"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          )}

          {isLoading ? (
            <div className="flex justify-center py-32">
              <div className="animate-spin w-10 h-10 border-2 border-primary border-t-transparent rounded-full" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-32">
              <Newspaper className="mx-auto text-muted-foreground mb-4" size={48} />
              <h3 className="text-xl font-bold text-white mb-2">
                {posts.length === 0 ? "No posts yet" : `No posts in "${activeCategory}"`}
              </h3>
              <p className="text-muted-foreground">
                {posts.length === 0 ? "Check back soon for news and updates." : "Try a different category."}
              </p>
            </div>
          ) : (
            <>
              {/* Featured post */}
              <motion.article
                key={featured.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="group relative mb-12 rounded-3xl overflow-hidden border border-white/10 hover:border-primary/40 transition-colors bg-[#0d1524]"
              >
                <Link href={`/blog/${featured.slug}`} className="block">
                  {featured.cover_image_url ? (
                    <div className="relative h-72 md:h-96 overflow-hidden">
                      <img
                        src={featured.cover_image_url}
                        alt={featured.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0d1524] via-[#0d1524]/50 to-transparent" />
                    </div>
                  ) : (
                    <div className="h-56 bg-gradient-to-br from-primary/20 to-secondary" />
                  )}
                  <div className="absolute bottom-0 left-0 right-0 p-8">
                    <div className="flex items-center gap-3 mb-3">
                      <CategoryBadge category={featured.category ?? "General"} />
                      {featured.published_at && (
                        <span className="flex items-center gap-1.5 text-xs text-gray-400">
                          <Calendar size={11} className="text-primary" />
                          {formatDate(featured.published_at)}
                        </span>
                      )}
                    </div>
                    <h2 className="text-2xl md:text-3xl font-display font-bold text-white group-hover:text-primary transition-colors leading-tight max-w-3xl">
                      {featured.title}
                    </h2>
                    {featured.excerpt && (
                      <p className="text-gray-300 mt-2 text-sm leading-relaxed line-clamp-2 max-w-2xl">{featured.excerpt}</p>
                    )}
                    <span className="inline-flex items-center gap-1.5 text-primary text-sm font-bold mt-4 group-hover:gap-2.5 transition-all">
                      Read article <ArrowRight size={14} />
                    </span>
                  </div>
                </Link>
              </motion.article>

              {/* Grid of remaining posts */}
              {rest.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {rest.map((post, i) => (
                    <motion.article
                      key={post.id}
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 + i * 0.07 }}
                      className="group bg-[#0d1524] border border-white/10 rounded-2xl overflow-hidden hover:border-primary/30 transition-colors flex flex-col"
                    >
                      <Link href={`/blog/${post.slug}`} className="flex flex-col flex-1">
                        <div className="overflow-hidden">
                          {post.cover_image_url ? (
                            <img
                              src={post.cover_image_url}
                              alt={post.title}
                              className="w-full h-44 object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                          ) : (
                            <div className="w-full h-44 bg-gradient-to-br from-primary/10 to-secondary flex items-center justify-center">
                              <Newspaper size={32} className="text-primary/30" />
                            </div>
                          )}
                        </div>
                        <div className="p-5 flex flex-col flex-1">
                          <div className="flex items-center gap-2 mb-3">
                            <CategoryBadge category={post.category ?? "General"} />
                          </div>
                          <h2 className="text-base font-display font-bold text-white group-hover:text-primary transition-colors leading-snug line-clamp-2">
                            {post.title}
                          </h2>
                          {post.excerpt && (
                            <p className="text-muted-foreground mt-2 text-sm leading-relaxed line-clamp-3 flex-1">{post.excerpt}</p>
                          )}
                          <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/5">
                            {post.published_at ? (
                              <span className="flex items-center gap-1.5 text-xs text-gray-500">
                                <Calendar size={11} className="text-primary" />
                                {formatDate(post.published_at)}
                              </span>
                            ) : <span />}
                            <span className="flex items-center gap-1 text-primary text-xs font-bold group-hover:gap-1.5 transition-all">
                              Read <ArrowRight size={12} />
                            </span>
                          </div>
                        </div>
                      </Link>
                    </motion.article>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </PageTransition>
    </>
  );
}
